import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import useTheme from "@mui/material/styles/useTheme";

import ErrorList from "components/error-list";
import { REOCCURRENCE_PERIOD_TYPE_DAILY } from "constants/appointments";
import {
  GLOBAL_PRACTICE_EVENTS_FEATURE,
  MULTIPLE_USERS_IN_CLINIC_EVENT_FEATURE
} from "constants/feature-flags";
import { VIEW_OWN_SCHEDULED_ONLY } from "constants/permissions";
import { ErrorsState } from "hooks/use-errors.hook";
import useUserRoles from "hooks/use-user-roles.hook";

import { AppointmentFormValues } from "../appointment-form.component";
import {
  AppointmentDatesField,
  NotesField,
  StaffField,
  TitleField
} from "../fields";
import AppointmentOptionalDateField from "../fields/appointment-dates-field/appointment-optional-date-field.component";
import ClinicField from "../fields/clinic-field.component";
import EventTypeField from "../fields/event-type-field.component";
import IsGlobalPracticeEventField from "../fields/is-global-practice-event-field.component";
import RecurrenceRuleEditorField from "../fields/recurrence-rule-editor-field.component";
import RecurringPeriodField from "../fields/recurring-period-field.component";
import StaffMembersField from "../fields/staff-members-field.component";

type EventFormViewProps = {
  defaultValues: AppointmentFormValues;
  nonFieldErrors: ErrorsState;
  multiClinic?: boolean;
  isClinicEvent?: boolean;
  isWorkShiftEvent?: boolean;
  showEventType?: boolean;
  recurrenceRuleEditorOpen?: boolean;
  setRecurrenceRuleEditorOpen?: (value: boolean) => void;
  appointmentId?: number | null;
  nonRRuleRecurrence?: boolean;
};

export default function EventFormView({
  defaultValues,
  nonFieldErrors,
  multiClinic = false,
  isClinicEvent = false,
  isWorkShiftEvent = false,
  showEventType,
  recurrenceRuleEditorOpen,
  setRecurrenceRuleEditorOpen,
  appointmentId,
  nonRRuleRecurrence
}: EventFormViewProps) {
  const theme = useTheme();
  const flags = useFlags();
  const isGlobalPracticeEventEnabled = flags[GLOBAL_PRACTICE_EVENTS_FEATURE];
  const isMultipleUsersInClinicEventFeatureEnabled =
    flags[MULTIPLE_USERS_IN_CLINIC_EVENT_FEATURE];

  const { userPermissions, isAdminOrBusinessOwner } = useUserRoles();
  const viewOwnScheduleOnly = userPermissions[VIEW_OWN_SCHEDULED_ONLY];

  const {
    setValue,
    control,
    clearErrors,
    formState: { errors }
  } = useFormContext<AppointmentFormValues>();

  useEffect(() => {
    return clearErrors;
  }, [clearErrors]);

  const [isRecurring, isGlobalPracticeEvent, recurrencePeriod] = useWatch({
    control,
    name: [
      "is_recurring",
      "is_global_practice_event",
      "recurrence_period"
    ] as const
  });

  useEffect(() => {
    if (isRecurring && !defaultValues.is_recurring) {
      setValue("event_end_date", null);
    } else {
      setValue("event_end_date", defaultValues.event_end_date);
    }
  }, [
    isRecurring,
    setValue,
    defaultValues.event_end_date,
    defaultValues.is_recurring
  ]);

  const showRRuleForNewEvent = !appointmentId;

  const showRRuleRecurringField = Boolean(
    !nonRRuleRecurrence || showRRuleForNewEvent
  );

  return (
    <Stack spacing={2} sx={{ maxWidth: 600, minHeight: 420 }}>
      <Stack
        spacing={2}
        sx={{
          boxShadow: theme.shadows[3],
          borderRadius: "6px",
          pt: 2,
          pb: 2,
          px: 2,
          background: "#fff"
        }}
      >
        <TitleField control={control} autoFocus={false} errors={errors} />
        {!isClinicEvent && (
          <StaffField
            disabled={viewOwnScheduleOnly}
            isGlobalPracticeEvent={isGlobalPracticeEvent}
            control={control}
            showActiveOnly={Boolean(
              !defaultValues.staff_member ||
                defaultValues.staff_member?.is_active
            )}
          />
        )}

        {isClinicEvent && isMultipleUsersInClinicEventFeatureEnabled && (
          <StaffMembersField control={control} showActiveOnly />
        )}

        {multiClinic && !isGlobalPracticeEvent && (
          <ClinicField required control={control} />
        )}
        <AppointmentDatesField
          label="Date Start"
          control={control}
          setValue={setValue}
          defaultValues={{
            appointment_date: defaultValues.appointment_date,
            start_time: defaultValues.start_time,
            end_time: defaultValues.end_time
          }}
          fromLabel="from"
          toLabel="to"
        />
        {isRecurring && (
          <AppointmentOptionalDateField
            label="Date End"
            name="event_end_date"
            control={control}
            setValue={setValue}
            required={recurrencePeriod === REOCCURRENCE_PERIOD_TYPE_DAILY}
          />
        )}
        {isAdminOrBusinessOwner &&
          !isWorkShiftEvent &&
          isGlobalPracticeEventEnabled &&
          multiClinic && <IsGlobalPracticeEventField control={control} />}

        {showEventType && <EventTypeField control={control} errors={errors} />}
        <>
          {showRRuleRecurringField ? (
            <RecurrenceRuleEditorField
              control={control}
              setValue={setValue}
              open={recurrenceRuleEditorOpen}
              setOpen={setRecurrenceRuleEditorOpen}
            />
          ) : (
            <RecurringPeriodField control={control} setValue={setValue} />
          )}
        </>
        <Box>
          <NotesField control={control} placeholder="Event notes" />
        </Box>
        <ErrorList errors={nonFieldErrors} />
      </Stack>
    </Stack>
  );
}
